import { useState, useContext, useEffect, useRef } from 'react';
import { t } from 'i18next';
import { usePreferenceUpdate } from '@sentara-health/sentara-api-hooks-core';
import { useForm } from 'react-hook-form';
import {
  EmailRegex,
  getMaxLength,
  errorhandler,
  domainForSegment,
  ANALYTICS_MESSAGES,
} from 'common';
import {
  ErrorMessage,
  InputField,
  Label,
  ModalOverlay,
  Checkbox,
  Headings,
  Link,
  Loader,
  Button,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { GlobalContext } from 'context';
import { EmailChangeProps } from './interface';
import { updatePreferenceHelper } from './updatePreferenceHelper';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { usePreferenceSearchAPI } from 'common/utils/PreferenceSearchAPIUtil';
const EmailChange = ({
  state,
  emailId,
  onClickCancel,
  onClickSubmit,
  handleSuccess,
  buttonLabel,
  customButton = false,
  memberView = false,
}: EmailChangeProps) => {
  const { loginMemberId } = useContext(GlobalContext);
  const { contentListingItemClicked } = useAdobeAnalytics();

  const trackContentListing = (
    contentId: string,
    contentTitle: string,
    itemPosition: number
  ) => {
    contentListingItemClicked({
      segment: `${domainForSegment}/profile`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [{ contentId, contentTitle, itemPosition }],
        },
      },
    });
  };

  const { updatePreference } = usePreferenceUpdate();
  const [isEmailNotificationOpen, setIsEmailNotificationOpen] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    handleSubmit,
    register,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      emailID: '',
      confirmEmailID: '',
      termsCondition: false,
    },
  });
  const emailInputRef = useRef<HTMLInputElement>(null); // Reference for the email input field
  const { fetchProfile } = usePreferenceSearchAPI();

  useEffect(() => {
    // Check if email is invalid on page load
    if (state?.isEmailValid === false && emailInputRef.current) {
      emailInputRef.current.focus(); // Focus on the email input field
      emailInputRef.current.classList.add('error-border'); // Add error-border class to highlight the field
    }
  }, [state?.isEmailValid]);

  function closePopUpModal() {
    setIsEmailNotificationOpen(false);
  }

  const onSubmit = async (data: any) => {
    const email = getValues('emailID');
    if (!isValidEmail(email) || email?.length < 0) {
      trackContentListing('604', ANALYTICS_MESSAGES.EMAIL_SAVE_FAILURE, 4);
      setError('emailID', {
        type: 'manual',
        message: t('profile.enterValidEmail') ?? '',
      });
    } else {
      clearErrors('emailID');
      setLoading(true);
      const apiPayload = updatePreferenceHelper({
        loginMemberId,
        status: 'Opt-in',
        email: [
          {
            system: 'email',
            value: email,
            use: 'personal',
            emailStatus: 'Valid',
          },
        ],
      });
      const response = await updatePreference(apiPayload);
      setLoading(false);
      if (response?.errorCode) {
        errorhandler(response?.errorCode);
        trackContentListing('604', ANALYTICS_MESSAGES.EMAIL_SAVE_FAILURE, 4);
      } else {
        trackContentListing('603', ANALYTICS_MESSAGES.EMAIL_SAVE_SUCCESS, 3);
        if (handleSuccess) {
          fetchProfile({
            onSuccess: () => {
              handleSuccess(email);
            },
          });
        }
        // Allow parent to handle next step if provided
        if (onClickSubmit) {
          onClickSubmit({ email } as any); // Pass data to parent if needed
        }
      }
    }
  };

  function isValidEmail(email: string) {
    return EmailRegex.test(email);
  }

  const onHandleCheckBox = (event: any) => {
    if (event.target.checked) {
      register('termsCondition', { required: false });
      clearErrors('termsCondition');
    } else {
      register('termsCondition', { required: true });
      setError('termsCondition', { type: 'required' });
    }
  };

  const email = watch('emailID');

  useEffect(() => {
    if (watch('confirmEmailID') !== '') {
      trigger('confirmEmailID');
    }
  }, [email, trigger]);

  return (
    <>
      {loading && <Loader />}
      <div className="row">
        <div className={`col-md-12 col-sm-12 col-12`}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={`mt-3`}>
              <div className={`row col-12 ${memberView ? '' : 'col-lg-10'}`}>
                <div className="col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="mb-3 position-relative">
                    <Label
                      dataTestId={'emailUpdateLabel'}
                      htmlFor="emailInput"
                      className="pb-2"
                    >
                      {t('common.emailAddress')}
                    </Label>
                    <InputField
                      id="emailInput"
                      autoComplete="email"
                      maxLength={getMaxLength.email}
                      className={`${
                        errors?.emailID?.type === 'required'
                          ? 'error-border'
                          : ''
                      }`}
                      placeholder={t('common.emaillblPlaceholder')}
                      data-testid="emailInput"
                      {...register('emailID', {
                        required: true,
                        pattern: {
                          value: EmailRegex,
                          message: 'Email criteria not match',
                        },
                      })}
                      aria-describedby="emailAddress_error"
                    />
                    {errors?.emailID && (
                      <div className="mt-1">
                        <ErrorMessage data-testid={'emailUpdate'}>
                          {t('common.emailIdValid')}
                        </ErrorMessage>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="mb-3 position-relative">
                    <Label
                      dataTestId="confirmEmailInputLabel"
                      htmlFor="confirmEmailAddress"
                      className="pb-2"
                    >
                      {t('activation.confirmEmailLabel')}
                    </Label>
                    <InputField
                      autoComplete="off"
                      maxLength={getMaxLength.email}
                      className={`${
                        errors.confirmEmailID ? 'error-border' : ''
                      }`}
                      placeholder={t('activation.confirmEmailLabel')}
                      id="confirmEmailAddress"
                      data-testid="confirmEmailInput"
                      {...register('confirmEmailID', {
                        required: true,
                        validate: (value) => {
                          return (
                            value.toLowerCase() ===
                            getValues('emailID').toLowerCase()
                          );
                        },
                      })}
                      aria-describedby="confirmEmail_error"
                    />
                    {errors.confirmEmailID && (
                      <div className="mt-1">
                        <ErrorMessage data-testid={'confirmEmailInputError'}>
                          {t('activation.confirmEmailError')}
                        </ErrorMessage>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <Paragraph
                  level={1}
                  variant="regular"
                  data-testid="emailDisclaimer"
                  text={
                    <>
                      <span className="pe-1">{t('profile.pleaseNote')}</span>
                      {t('profile.emailDisclaimer')}
                    </>
                  }
                />
              </div>
              <div className="d-flex">
                <div className="position-relative">
                  <div className="d-flex justify-content-center">
                    <Checkbox
                      id="emailNotificationsAndConsentCheck"
                      className={
                        errors.termsCondition ? 'error-input-bdr' : null
                      }
                      type="checkbox"
                      {...register('termsCondition', {
                        required: true,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          onHandleCheckBox(e);
                          setValue('termsCondition', e.target.checked);
                        },
                      })}
                      dataTestId="emailNotificationConsentCheckBox"
                    >
                      {t('profile.termsOfUsePart')}{' '}
                      <Link
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          setIsEmailNotificationOpen(true);
                        }}
                        className="text-decoration-underline small-link"
                        dataTestId="emailNotificationConsentTitle"
                      >
                        {t('profile.emailNotificationsAndConsent')}
                      </Link>
                      {t('common.dot')}
                    </Checkbox>
                  </div>
                  {errors.termsCondition && (
                    <ErrorMessage dataTestId={'emailConsent'}>
                      {t('profile.emailConsent')}
                    </ErrorMessage>
                  )}
                </div>
              </div>
              <hr />
              <div className="row d-flex justify-content-start align-items-center py-2">
                <div className="col-sm-auto col-12 mb-sm-0 mb-4">
                  {!customButton ? (
                    <Button
                      data-testid={'saveButton'}
                      variant="primary_btn"
                      disabled={
                        !(
                          watch('termsCondition') === true &&
                          watch('emailID') !== '' &&
                          watch('confirmEmailID')
                        )
                      }
                      type={'submit'}
                    >
                      {t('common.save')}
                    </Button>
                  ) : (
                    <Button
                      dataTestId={'saveNextButton'}
                      variant="primary_btn"
                      disabled={
                        !(
                          watch('termsCondition') === true &&
                          watch('emailID') !== '' &&
                          watch('confirmEmailID')
                        )
                      }
                      type={'button'}
                      onClick={async (
                        e: React.MouseEvent<HTMLButtonElement>
                      ) => {
                        const isTermsAccepted =
                          watch('termsCondition') === true;
                        const hasEmail = watch('emailID') !== '';
                        const hasConfirmEmail = watch('confirmEmailID');

                        if (isTermsAccepted && hasEmail && hasConfirmEmail) {
                          try {
                            await handleSubmit(onSubmit)(); // Trigger form submission and wait for result
                            if (onClickSubmit) {
                              onClickSubmit(e); // Proceed to next step if submission succeeds
                            }
                          } catch (error) {
                            // Handle any form validation or submission errors if needed
                            console.error('Form submission failed:', error);
                          }
                        } else {
                          e.preventDefault(); // Prevent default action if conditions are not met
                        }
                      }}
                    >
                      {buttonLabel}
                    </Button>
                  )}
                </div>
                <div className="col-sm-auto col-12 mb-sm-0">
                  <Button
                    dataTestId={'emailCancelButton'}
                    variant="secondary_btn"
                    onClick={onClickCancel}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
        <ModalOverlay
          isOpen={isEmailNotificationOpen}
          onClose={closePopUpModal}
          overlayInfo="Go paperless overlay"
        >
          <Headings
            level={2}
            text={t('profile.emailNotificationsAndConsent')}
            variant="regular"
            dataTestId="emailNotificationsAndConsentTitle"
          />
          <div
            data-testid="emailNotificationMessage"
            className="phone_notify_msg_wrapper"
          >
            <div className="d-flex flex-column gap-3">
              <Paragraph
                text={t('profile.emailNotificationMessage')}
                variant="regular"
                data-testid="emailNotificationMessage"
                className="lineBrake"
              />
            </div>
          </div>
        </ModalOverlay>
      </div>
    </>
  );
};

export default EmailChange;
